<template>
  <div class="account-manager-card">
    <div class="account-manager-card__header">
      <div class="account-manager-card__avatar-wrap">
        <img
          :src="avatarSrc"
          :alt="name || 'Account manager'"
          class="account-manager-card__avatar"
          width="96"
          height="96"
        />
        <span class="account-manager-card__status-dot" aria-hidden="true" />
      </div>
    </div>

    <div class="account-manager-card__body">
      <h2 class="account-manager-card__name">{{ name }}</h2>
      <p class="account-manager-card__role">{{ $t('lang.label.accountmanager') }}</p>
      <div class="account-manager-card__badge">• {{ $t('lang.label.your_dedicated_contact') }}</div>

      <div class="account-manager-card__contacts">
        <div
          class="account-manager-card__row"
          :class="{ 'account-manager-card__row--copied': emailCopied, 'account-manager-card__row--clickable': !emailCopied && emailTrimmed }"
          role="button"
          :tabindex="emailCopied || !emailTrimmed ? -1 : 0"
          @click.stop="onCopyEmail"
          @keydown.enter.prevent="onCopyEmail"
          @keydown.space.prevent="onCopyEmail"
        >
          <div class="account-manager-card__icon-wrap">
            <i
              v-if="!emailCopied"
              class="fas fa-envelope account-manager-card__icon"
              aria-hidden="true"
            />
            <span v-else class="account-manager-card__check-mark" aria-hidden="true" title="Copié">✓</span>
          </div>
          <div class="account-manager-card__row-text">
            <span class="account-manager-card__label">{{ $t('lang.label.email') }}</span>
            <span class="account-manager-card__value">{{ email }}</span>
          </div>
          <i
            v-if="!emailCopied"
            class="fas fa-chevron-right account-manager-card__chevron"
            aria-hidden="true"
          />
        </div>

        <div
          class="account-manager-card__row"
          :class="{ 'account-manager-card__row--copied': phoneCopied, 'account-manager-card__row--clickable': !phoneCopied && phoneTrimmed }"
          role="button"
          :tabindex="phoneCopied || !phoneTrimmed ? -1 : 0"
          @click.stop="onCopyPhone"
          @keydown.enter.prevent="onCopyPhone"
          @keydown.space.prevent="onCopyPhone"
        >
          <div class="account-manager-card__icon-wrap">
            <i
              v-if="!phoneCopied"
              class="fas fa-phone account-manager-card__icon"
              aria-hidden="true"
            />
            <span v-else class="account-manager-card__check-mark" aria-hidden="true" title="Copié">✓</span>
          </div>
          <div class="account-manager-card__row-text">
            <span class="account-manager-card__label">{{ $t('lang.label.phone') }}</span>
            <span class="account-manager-card__value">{{ phone }}</span>
          </div>
          <i
            v-if="!phoneCopied"
            class="fas fa-chevron-right account-manager-card__chevron"
            aria-hidden="true"
          />
        </div>
      </div>

      <div class="account-manager-card__divider" />

      <p class="account-manager-card__footer-lead">
        <strong>{{ $t('lang.label.response_am') }}</strong>
      </p>
      <p class="account-manager-card__footer-desc">
        {{ name }} {{ $t('lang.label.know_account_and_campaigns') }}
      </p>
    </div>
  </div>
</template>

<script>
const AVATAR_PLACEHOLDER =
  "data:image/svg+xml;base64,PHN2ZyBmaWxsPSJub25lIiBoZWlnaHQ9IjMyIiB3aWR0aD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHJ4PSIxNiIgZmlsbD0iIzkwY2FmOSIvPjxjaXJjbGUgY3g9IjE2IiBjeT0iMTIiIHI9IjUiIGZpbGw9IndoaXRlIi8+PHBhdGggZD0iTTE2IDE4Yy02LjYgMC0xMiA0LjItMTIgOXYxYzAgMS4xLjkgMiAyIDJoMjhjMS4xIDAgMi0uOSAyLTJ2LTFjMC00LjgtNS40LTktMTItOXoiIGZpbGw9IndoaXRlIi8+PC9zdmc+";

export default {
  name: "AccountManagerModal",

  props: {
    name: {
      type: String,
      default: "",
    },
    email: {
      type: [String, Number],
      default: "",
    },
    phone: {
      type: [String, Number],
      default: "",
    },
    photoUrl: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      emailCopied: false,
      phoneCopied: false,
    };
  },

  computed: {
    avatarSrc() {
      const u = this.photoUrl != null ? String(this.photoUrl).trim() : "";
      return u ? u : AVATAR_PLACEHOLDER;
    },
    emailTrimmed() {
      const v = this.email;
      if (v === null || v === undefined) return "";
      return String(v).trim();
    },
    phoneTrimmed() {
      const v = this.phone;
      if (v === null || v === undefined) return "";
      return String(v).trim();
    },
  },

  methods: {
    resetCopyState() {
      this.emailCopied = false;
      this.phoneCopied = false;
    },

    async copyText(text) {
      if (!text) return false;
      try {
        if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
          await navigator.clipboard.writeText(text);
          return true;
        }
      } catch (e) {
        /* continue fallback */
      }
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.setAttribute("aria-hidden", "true");
        ta.style.position = "fixed";
        ta.style.top = "0";
        ta.style.left = "0";
        ta.style.width = "1px";
        ta.style.height = "1px";
        ta.style.padding = "0";
        ta.style.border = "none";
        ta.style.outline = "none";
        ta.style.boxShadow = "none";
        ta.style.background = "transparent";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ta.setSelectionRange(0, text.length);
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return !!ok;
      } catch (e2) {
        return false;
      }
    },

    async onCopyEmail() {
      if (this.emailCopied) return;
      const text = this.emailTrimmed;
      if (!text) return;
      const ok = await this.copyText(text);
      if (ok) this.$set(this, "emailCopied", true);
    },

    async onCopyPhone() {
      if (this.phoneCopied) return;
      const text = this.phoneTrimmed;
      if (!text) return;
      const ok = await this.copyText(text);
      if (ok) this.$set(this, "phoneCopied", true);
    },
  },
};
</script>

<style scoped>
.account-manager-card {
  max-width: 800px;
  margin: 0 auto;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  font-family: inherit;
}

.account-manager-card__header {
  position: relative;
  height: 120px;
  background: linear-gradient(90deg, #4a47d1 0%, #3b38b5 100%);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 0;
}

.account-manager-card__avatar-wrap {
  position: relative;
  transform: translateY(50%);
  margin-bottom: 0;
}

.account-manager-card__avatar {
  display: block;
  width: 96px;
  height: 96px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid #fff;
  background: #f0f0ff;
}

.account-manager-card__status-dot {
  position: absolute;
  right: 4px;
  bottom: 4px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #22c55e;
  border: 3px solid #fff;
  box-sizing: border-box;
}

.account-manager-card__body {
  padding: 64px 24px 28px;
  text-align: center;
}

.account-manager-card__name {
  margin: 0 0 6px;
  font-size: 1.25rem;
  font-weight: 700;
  color: #000;
  line-height: 1.3;
}

.account-manager-card__role {
  margin: 0 0 14px;
  font-size: 0.9375rem;
  color: #6b7280;
}

.account-manager-card__badge {
  display: inline-block;
  padding: 6px 14px;
  border-radius: 999px;
  background: #f0f0ff;
  color: #4a47d1;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  margin-bottom: 24px;
}

.account-manager-card__contacts {
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.account-manager-card__row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  cursor: default;
  user-select: text;
}

.account-manager-card__row--clickable {
  cursor: pointer;
  user-select: none;
}

.account-manager-card__row--clickable:hover {
  border-color: #c7d2fe;
  background: #fafbff;
}

.account-manager-card__row--clickable:focus {
  outline: 2px solid #4a47d1;
  outline-offset: 2px;
}

.account-manager-card__row--copied {
  cursor: default;
  user-select: text;
}

.account-manager-card__icon-wrap {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #f0f0ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.account-manager-card__icon {
  font-size: 1rem;
  color: #4a47d1;
}

.account-manager-card__check-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1;
  color: #16a34a;
}

.account-manager-card__row-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.account-manager-card__label {
  font-size: 0.6875rem;
  font-weight: 600;
  color: #6b7280;
  letter-spacing: 0.04em;
}

.account-manager-card__value {
  font-size: 0.9375rem;
  color: #111827;
  word-break: break-word;
}

.account-manager-card__chevron {
  flex-shrink: 0;
  font-size: 0.75rem;
  color: #d1d5db;
}

.account-manager-card__divider {
  height: 0.5px;
  background: #e5e7eb;
  margin: 22px 0 18px;
}

.account-manager-card__footer-lead {
  margin: 0 0 10px;
  font-size: 0.9375rem;
  color: #374151;
}

.account-manager-card__footer-desc {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #6b7280;
}
</style>
